import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../config/api';

// Types
export interface TimeSeriesData {
  period: string;
  post_count: number;
  total_likes: number;
  total_comments: number;
  total_views: number;
  avg_duration: number;
}

export interface EngagementData {
  post_type: string;
  avg_likes: number;
  avg_comments: number;
  avg_views: number;
  total_posts: number;
}

export interface HashtagData {
  hashtag: string;
  usage_count: number;
  avg_likes: number;
  avg_comments: number;
}

export interface SentimentData {
  source: 'instagram' | 'google' | 'tiktok' | 'gcc';
  sentiment: string;
  count: number;
  replied_count: number;
}

export interface SentimentTimeSeriesData {
  period: string;
  source: 'instagram' | 'google' | 'tiktok' | 'gcc';
  positive_count: number;
  neutral_count: number;
  negative_count: number;
}

export interface CommenterTimeSeriesData {
  period: string;
  unique_commenters: number;
  total_comments: number;
  commented_posts: number;
  avg_comments_per_user: number;
  total_replies: number;
  users_with_replies: number;
  total_active_commenters: number;
  median_comments_per_user: number;
  mean_comments_per_user: number;
  max_comments_per_user: number;
}

export interface TopCommenterData {
  user_id: string;
  username: string;
  total_comments: number;
  unique_posts_commented: number;
  reply_count: number;
  avg_likes_per_comment: number;
  positive_comments: number;
  negative_comments: number;
  neutral_comments: number;
  positive_sentiment_percentage: number;
}

export interface PostOwnerAnalytics {
  owner_username: string;
  owner_full_name: string;
  period: string;
  total_comments: number;
  posts_with_comments: number;
  total_comment_likes: number;
  total_post_likes: number;
  avg_likes_per_comment: number;
  positive_comments: number;
  negative_comments: number;
  neutral_comments: number;
  high_priority_comments: number;
  medium_priority_comments: number;
  low_priority_comments: number;
  replied_comments: number;
  reply_rate: number;
}

interface AnalyticsState {
  postsTimeSeries: TimeSeriesData[];
  engagement: EngagementData[];
  hashtags: HashtagData[];
  commentsData: any[];
  sentimentData: SentimentData[];
  sentimentTimeSeries: SentimentTimeSeriesData[];
  commenterTimeSeries: CommenterTimeSeriesData[];
  topCommenters: TopCommenterData[];
  postOwnerAnalytics: PostOwnerAnalytics[];
  loading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  postsTimeSeries: [],
  engagement: [],
  hashtags: [],
  commentsData: [],
  sentimentData: [],
  sentimentTimeSeries: [],
  commenterTimeSeries: [],
  topCommenters: [],
  postOwnerAnalytics: [],
  loading: false,
  error: null,
};

// Async Thunks
export const fetchPostsAnalytics = createAsyncThunk(
  'analytics/fetchPostsAnalytics',
  async ({ startDate, endDate, timeRange, brand_id }: { 
    startDate: string; 
    endDate: string; 
    timeRange: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/posts`, {
      params: { startDate, endDate, timeRange, brand_id },
    });
    return response.data;
  }
);

export const fetchEngagementMetrics = createAsyncThunk(
  'analytics/fetchEngagementMetrics',
  async ({ startDate, endDate, brand_id }: { 
    startDate: string; 
    endDate: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/posts/engagement`, {
      params: { startDate, endDate, brand_id },
    });
    return response.data;
  }
);

export const fetchHashtagAnalytics = createAsyncThunk(
  'analytics/fetchHashtagAnalytics',
  async ({ startDate, endDate, brand_id }: { 
    startDate: string; 
    endDate: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/hashtags`, {
      params: { startDate, endDate, brand_id },
    });
    return response.data;
  }
);

export const fetchCommentsAnalytics = createAsyncThunk(
  'analytics/fetchCommentsAnalytics',
  async ({ startDate, endDate, timeRange, brand_id }: { 
    startDate: string; 
    endDate: string; 
    timeRange: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/comments`, {
      params: { startDate, endDate, timeRange, brand_id },
    });
    return response.data;
  }
);

export const fetchSentimentAnalytics = createAsyncThunk(
  'analytics/fetchSentimentAnalytics',
  async ({ startDate, endDate, brand_id }: { 
    startDate: string; 
    endDate: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/sentiment`, {
      params: { startDate, endDate, brand_id },
    });
    return response.data;
  }
);

export const fetchSentimentTimeSeries = createAsyncThunk(
  'analytics/fetchSentimentTimeSeries',
  async ({ startDate, endDate, timeRange, brand_id }: { 
    startDate: string; 
    endDate: string; 
    timeRange: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/sentiment/timeseries`, {
      params: { startDate, endDate, timeRange, brand_id },
    });
    return response.data;
  }
);

export const fetchCommenterAnalytics = createAsyncThunk(
  'analytics/fetchCommenterAnalytics',
  async ({ startDate, endDate, timeRange, brand_id }: { 
    startDate: string; 
    endDate: string; 
    timeRange: string;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/commenters`, {
      params: { startDate, endDate, timeRange, brand_id },
    });
    return response.data;
  }
);

export const fetchTopCommenters = createAsyncThunk(
  'analytics/fetchTopCommenters',
  async ({ startDate, endDate, limit, brand_id }: { 
    startDate: string; 
    endDate: string; 
    limit: number;
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/commenters/top`, {
      params: { startDate, endDate, limit, brand_id },
    });
    return response.data;
  }
);

export const fetchPostOwnerAnalytics = createAsyncThunk(
  'analytics/fetchPostOwnerAnalytics',
  async ({ startDate, endDate, timeRange, brand_id }: { 
    startDate: string; 
    endDate: string; 
    timeRange: 'daily' | 'weekly' | 'monthly';
    brand_id?: string;
  }) => {
    const response = await api.get(`/analytics/post-owners`, {
      params: { startDate, endDate, timeRange, brand_id },
    });
    return response.data;
  }
);

// Slice
const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Posts Analytics
      .addCase(fetchPostsAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostsAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.postsTimeSeries = action.payload;
      })
      .addCase(fetchPostsAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch posts analytics';
      })
      // Engagement Metrics
      .addCase(fetchEngagementMetrics.fulfilled, (state, action) => {
        state.engagement = action.payload;
      })
      // Hashtag Analytics
      .addCase(fetchHashtagAnalytics.fulfilled, (state, action) => {
        state.hashtags = action.payload;
      })
      // Comments Analytics
      .addCase(fetchCommentsAnalytics.fulfilled, (state, action) => {
        state.commentsData = action.payload;
      })
      // Sentiment Analytics
      .addCase(fetchSentimentAnalytics.fulfilled, (state, action) => {
        console.log('Sentiment Analytics Response:', action.payload);
        state.sentimentData = action.payload;
      })
      // Sentiment Time Series
      .addCase(fetchSentimentTimeSeries.fulfilled, (state, action) => {
        state.sentimentTimeSeries = action.payload;
      })
      // Commenter Analytics
      .addCase(fetchCommenterAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCommenterAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.commenterTimeSeries = action.payload;
      })
      .addCase(fetchCommenterAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch commenter analytics';
      })
      // Top Commenters
      .addCase(fetchTopCommenters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopCommenters.fulfilled, (state, action) => {
        state.loading = false;
        state.topCommenters = action.payload;
      })
      .addCase(fetchTopCommenters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch top commenters';
      })
      // Post Owner Analytics
      .addCase(fetchPostOwnerAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPostOwnerAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.postOwnerAnalytics = action.payload;
      })
      .addCase(fetchPostOwnerAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch post owner analytics';
      });
  },
});

export default analyticsSlice.reducer; 